import axios from "../../api/axiosInstance";
import { STATUS, getProjectId, extractErrorInfo } from "./sharedfunction";

/**
 * Fetch list of environments for the current project
 * @returns {Promise<{status: number, data?: Array, message?: string, error?: string}>}
 */
export async function fetchEnvironmentList() {
  try {
    const projectId = getProjectId();
    
    if (!projectId) {
      return {
        status: STATUS.ERROR,
        error: "Project ID is required",
      };
    }

    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/${projectId}/environment/fetch-environment-list`,
      {
        withCredentials: true,
      }
    );

    if (response?.data?.success) {
      return {
        status: STATUS.SUCCESS,
        data: response?.data?.data || [],
        message: response?.data?.message,
      };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
      errors: response?.data?.errors,
    };
  } catch (error) {
    console.error("Error fetching environment list:", error);
    return extractErrorInfo(error);
  }
}

/**
 * Create a new environment
 * @param {Object} params - Environment creation parameters
 * @param {string} params.name - Environment name (required)
 * @param {string} [params.description] - Environment description (optional)
 * @returns {Promise<{status: number, data?: Object, message?: string, error?: string}>}
 */
export async function createEnvironment({ name, description } = {}) {
  try {
    const projectId = getProjectId();
    
    if (!projectId) {
      return {
        status: STATUS.ERROR,
        error: "Project ID is required",
      };
    }

    if (!name || name.trim().length === 0) {
      return {
        status: STATUS.ERROR,
        error: "Environment name is required",
      };
    }

    const response = await axios.post(
      `${process.env.REACT_APP_API_URL}/${projectId}/environment/createenvironment`,
      { name, description },
      {
        withCredentials: true,
      }
    );

    if (response?.data?.success) {
      return {
        status: STATUS.SUCCESS,
        data: response?.data?.data || {},
        message: response?.data?.message,
      };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
      errors: response?.data?.errors,
    };
  } catch (error) {
    console.error("Error creating environment:", error);
    return extractErrorInfo(error);
  }
}

/**
 * Update an existing environment
 * @param {Object} params - Environment update parameters
 * @param {string} params.id - Environment ID (required)
 * @param {Object} params.data - Update data
 * @param {string} [params.data.name] - Environment name (optional)
 * @param {string} [params.data.description] - Environment description (optional)
 * @returns {Promise<{status: number, data?: Object, message?: string, error?: string}>}
 */
export async function updateEnvironment({ id, data } = {}) {
  try {
    const projectId = getProjectId();
    
    if (!projectId) {
      return {
        status: STATUS.ERROR,
        error: "Project ID is required",
      };
    }

    if (!id) {
      return {
        status: STATUS.ERROR,
        error: "Environment ID is required",
      };
    }

    const response = await axios.put(
      `${process.env.REACT_APP_API_URL}/${projectId}/environment/updateenvironment/${id}`,
      data,
      {
        withCredentials: true,
      }
    );

    if (response?.data?.success) {
      return {
        status: STATUS.SUCCESS,
        data: response?.data?.data || {},
        message: response?.data?.message,
      };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
      errors: response?.data?.errors,
    };
  } catch (error) {
    console.error("Error updating environment:", error);
    return extractErrorInfo(error);
  }
}

/**
 * Delete one or more environments
 * @param {Object} params - Environment deletion parameters
 * @param {Array<string>} params.environmentIds - Array of environment IDs to delete (required)
 * @returns {Promise<{status: number, data?: Object, message?: string, error?: string}>}
 */
export async function deleteEnvironment({ environmentIds = [] } = {}) {
  try {
    const projectId = getProjectId();
    
    if (!projectId) {
      return {
        status: STATUS.ERROR,
        error: "Project ID is required",
      };
    }

    if (!environmentIds || environmentIds.length === 0) {
      return {
        status: STATUS.ERROR,
        error: "At least one environment ID is required",
      };
    }

    const response = await axios.delete(
      `${process.env.REACT_APP_API_URL}/${projectId}/environment/deleteenvironment`,
      {
        data: { environmentIds },
        withCredentials: true,
      }
    );

    if (response?.data?.success) {
      return {
        status: STATUS.SUCCESS,
        data: response?.data?.data || {},
        message: response?.data?.message,
      };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
      errors: response?.data?.errors,
    };
  } catch (error) {
    console.error("Error deleting environment:", error);
    return extractErrorInfo(error);
  }
}

